import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Heart } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "Programs", href: "/#programs" },
    { name: "Health Analysis", href: "/health-analysis" },
    { name: "Calculators", href: "/calculators" },
    { name: "Reviews", href: "/reviews" },
    { name: "Knowledge Hub", href: "/resources" },
    { name: "How We Work", href: "/how-we-work" },
    { name: "Find My Program", href: "/find-my-program" },
    { name: "Blog", href: "/blog" },
    { name: "Book Free Consultation", href: "/book-consultation?from=footer" }
  ];

  const hubLinks = [
    { name: "Diabetes & Metabolic Health", href: "/hub/diabetes-metabolic-health" },
    { name: "Metabolic & Weight Health", href: "/hub/metabolic-weight-health" },
    { name: "Women's & Reproductive Health", href: "/hub/womens-reproductive-health" },
    { name: "Pregnancy & Postpartum", href: "/hub/pregnancy-postpartum" },
    { name: "Fitness & Body Transformation", href: "/hub/fitness-body-transformation" }
  ];

  const legalLinks = [
    { name: "Editorial & Medical Review Policy", href: "/editorial-policy" },
    { name: "Medical Disclaimer", href: "/medical-disclaimer" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Refund Policy", href: "/refund-policy" }
  ];

  return (
    <footer className="bg-slate-950 border-t border-slate-800">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="py-12 sm:py-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <h3 className="text-xl sm:text-2xl font-bold">
                <span className="bg-gradient-to-r from-blue-400 via-orange-400 to-emerald-400 bg-clip-text text-transparent">
                  TCUBE FITNESS
                </span>
                <span className="text-blue-400 text-lg sm:text-xl align-top relative -top-1.5 ml-1 font-normal">™</span>
              </h3>
            </Link>
            <p className="text-slate-400 text-sm mb-4">
              Doc-Led Health, Nutrition & Fitness
            </p>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Transform your health with personalized fitness coaching and nutrition plans designed by medical professionals.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              <a 
                href="https://www.instagram.com/tcubefitness/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 hover:bg-pink-500/20 rounded-lg flex items-center justify-center text-slate-400 hover:text-pink-400 transition-all"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a 
                href="https://www.instagram.com/layman_doctor/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 hover:bg-pink-500/20 rounded-lg flex items-center justify-center text-slate-400 hover:text-pink-400 transition-all"
                aria-label="Dr. Pooja's Instagram"
              >
                <Heart className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Health Hubs */}
          <div>
            <h4 className="text-white font-semibold mb-4">Health Hubs</h4>
            <ul className="space-y-3">
              {hubLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-slate-400 hover:text-white text-sm transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="https://wa.me/919515901777" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition-colors"
                >
                  <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm">+91 9515901777</p>
                    <p className="text-xs text-slate-500">WhatsApp Available</p>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:support@tcubefitness.com"
                  className="flex items-start gap-3 text-slate-400 hover:text-blue-400 transition-colors"
                >
                  <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">support@tcubefitness.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm">100% Online</p>
                    <p className="text-xs text-slate-500">Serving Clients Worldwide</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="py-6 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-xs sm:text-sm text-center sm:text-left">
            © 2026 TCUBE FITNESS™. All rights reserved.
          </p>
          <p className="text-slate-600 text-xs sm:text-sm text-center sm:text-right">
            Treat • Train • Transform
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
